/**
 * Altar del Perfume — community rail.
 *
 * The card the rail has settled on is the one that grows. Emphasis marks position, so it is
 * derived from where the cards actually sit rather than fixed to one block in the editor.
 *
 * An IntersectionObserver whose root is the rail, with the root inset to a narrow band at its
 * centre, fires exactly when a different card crosses the middle — no scroll listener, no work
 * between crossings. The measuring pass then picks the card nearest the centre, which keeps the
 * choice unambiguous when two cards overlap the band at once.
 *
 * Progressive enhancement: the block marked "Destacar" carries `data-active` from Liquid, so
 * with scripting off the section still reads as designed. This takes over on connect.
 */
class AltarUgcRail extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('[data-altar-ugc-rail]');
    if (!this.rail || !('IntersectionObserver' in window)) return;

    this.items = Array.from(this.rail.children);
    if (this.items.length === 0) return;

    this.observer = new IntersectionObserver(() => this.select(), {
      root: this.rail,
      // Leaves roughly the middle 8% of the rail as the decision band.
      rootMargin: '0px -46% 0px -46%',
      threshold: 0,
    });

    for (const item of this.items) this.observer.observe(item);

    // A width change moves the centre line onto a different card.
    this.onResize = () => this.select();
    window.addEventListener('resize', this.onResize, { passive: true });

    this.select();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    window.removeEventListener('resize', this.onResize);
  }

  /** The card whose centre sits closest to the rail's centre is the active one. */
  select() {
    const rail = this.rail.getBoundingClientRect();
    const centre = rail.left + rail.width / 2;

    let winner = null;
    let shortest = Infinity;

    for (const item of this.items) {
      const box = item.getBoundingClientRect();
      const distance = Math.abs(box.left + box.width / 2 - centre);
      if (distance < shortest) {
        shortest = distance;
        winner = item;
      }
    }

    for (const item of this.items) item.toggleAttribute('data-active', item === winner);
  }
}

if (!customElements.get('altar-ugc-rail')) {
  customElements.define('altar-ugc-rail', AltarUgcRail);
}
